import {usePlugins} from '@github-ui/copilot-chat/plugin/registry'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {ErrorFallback} from '../ErrorFallback'
import type {PreviewableContent} from './content-preview-types'
import {CreateIssuePreview} from './create-issue-preview/CreateIssuePreview'
import {ExistingIssuePreview} from './create-issue-preview/ExistingIssuePreview'
import {UpdateIssuePreview} from './create-issue-preview/UpdateIssuePreview'
import {DiffPreview} from './DiffPreview'
import {FilePreview} from './FilePreview'
import {ImagePreview} from './ImagePreview'
import {IssuePreview} from './IssuePreview'
import {JobResultPreview} from './JobResultPreview'

interface ContentPreviewItemProps {
  isOpening: boolean
  onClose: (closePreview?: boolean) => void
  previewItem: PreviewableContent
}

export function ContentPreviewItem({isOpening, onClose, previewItem}: ContentPreviewItemProps) {
  const plugins = usePlugins()
  let item = null
  switch (previewItem.type) {
    case 'file':
      item = <FilePreview isPreviewOpening={isOpening} file={previewItem} onClose={onClose} />
      break
    case 'file-diff':
      item = <DiffPreview fileDiff={previewItem} />
      break
    case 'issue':
      item = <IssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'new-issue':
      item = <CreateIssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'update-issue':
      item = <UpdateIssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'existing-issue':
      item = <ExistingIssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'image':
      item = <ImagePreview image={previewItem} />
      break
    case 'plugin-content':
      for (const plugin of plugins) {
        const PluginPreviewComponent = plugin.previewComponents?.[previewItem.reference.type]
        if (PluginPreviewComponent) {
          item = <PluginPreviewComponent reference={previewItem.reference} />
          break
        }
      }
      break
    case 'job-result':
      item = <JobResultPreview jobResult={previewItem} />
      break
  }

  return item ? (
    <ErrorBoundary fallback={<ErrorFallback regionName="This workbench item" />}>{item}</ErrorBoundary>
  ) : null
}
